import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { mdmBreadcrumbs } from "src/common/mdmBreadcrumbs";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { msgQueueStore } from "src/components/MsgQueue/msgQueueStore";
import { ZRoleSaveData } from "src/types";
import { ZRole } from "src/types/ZRole";

import {
  createRole,
  deleteRole as deleteRoleApi,
  loadRole,
  updateRole,
} from "../../../../../business/role/apiRoles";

export class SingleRoleStore {
  data: RemoteData<ZRole | null> = { status: "none" };

  setData(newData: RemoteData<ZRole | null>) {
    this.data = newData as RemoteData<ZRole | null>;
  }

  loading = false;

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  constructor() {
    makeAutoObservable(this);
  }

  loadRole = async (roleId: string | undefined) => {
    if (!roleId) {
      this.setData({ status: "ready", result: null });
      return;
    }
    this.setData({ status: "wait" });
    try {
      const role = await loadRole(roleId);
      this.setData({ status: "ready", result: role });
    } catch (error) {
      this.setData({ status: "error", error });
    } finally {
      this.setLoading(false);
    }
  };

  get crumbs(): BreadcrumbItem[] {
    const { data } = this;
    let name = "...";
    if (data.status === "ready") {
      name = data.result?.name ?? "Новая роль";
    }
    return mdmBreadcrumbs([
      { type: "UsersAndRoles" },
      { type: "AdminRoles" },
      { type: "Name", name },
    ]);
  }

  get roleId() {
    if (this.data.status !== "ready" || this.data.result === null) {
      return undefined;
    }
    return this.data.result.id;
  }

  saveRole = async (roleData: ZRoleSaveData): Promise<ZRole | null> => {
    const { roleId } = this;
    const isNew = !roleId;

    this.setLoading(true);
    try {
      const result = isNew
        ? await createRole(roleData)
        : await updateRole(roleId || "", roleData);
      this.setData({ status: "ready", result });
      const msg = isNew
        ? t("Created successfully [entity]", { entity: t("Role") })
        : t("Updated successfully [entity]", { entity: t("Role") });
      msgQueueStore.add({
        type: "success",
        message: msg,
      });
      return result;
    } catch (error) {
      onError(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  deleteRole = async () => {
    const { roleId } = this;
    if (!roleId) {
      return;
    }
    /**
     * try catch обрабатывается в DeleteConfirm
     */
    await deleteRoleApi(roleId);
    this.setData({ status: "ready", result: null });
  };

  /**
   * сейчас singleRoleStore используется как глобальный store
   * для всех страниц ролей
   * поэтому нужно очищать данные при unmount компонента
   */
  clear() {
    this.setData({ status: "none" });
  }
}

export const singleRoleStore = new SingleRoleStore();
